import { withImmediateValueOrDefault } from "applesauce-core";
import { insertEventIntoDescendingList } from "applesauce-core/helpers";
import { createTimelineLoader, TimelineLoader } from "applesauce-loaders/loaders";
import { NostrEvent } from "nostr-tools";
import { combineLatest, map, Observable, of, scan, shareReplay, switchMap, throttleTime } from "rxjs";

import { shareAndHold } from "../../helpers/observable";
import { GAME_KIND, MOVE_KIND } from "../../views/games/types";
import accounts from "../accounts";
import { cacheRequest } from "../event-cache";
import { eventStore } from "../event-store";
import pool from "../pool";
import { inboxes$ } from "./common";

/** Timeline loader for game notifications from the user's inboxes */
export const gameNotificationsLoader$: Observable<TimelineLoader | null> = combineLatest([
  accounts.active$,
  inboxes$,
]).pipe(
  map(([account, inboxes]) => {
    if (!account || inboxes.length === 0) return null;

    return createTimelineLoader(
      pool,
      inboxes,
      [
        {
          "#p": [account.pubkey],
          kinds: [MOVE_KIND, GAME_KIND],
        },
      ],
      {
        limit: 100,
        cache: cacheRequest,
        eventStore,
      },
    );
  }),
  shareReplay(1),
);

/** Observable stream of game notification events (challenges, moves, results directed at user) */
export const gameNotifications$: Observable<NostrEvent[]> = accounts.active$.pipe(
  switchMap((account) => {
    if (!account) return of([]);

    return eventStore
      .filters({
        kinds: [MOVE_KIND, GAME_KIND],
        "#p": [account.pubkey],
      })
      .pipe(
        scan((events, event) => {
          if (event.pubkey === account.pubkey) return events;
          if (events.some((e) => e.id === event.id)) return events;
          return insertEventIntoDescendingList(events, event);
        }, [] as NostrEvent[]),
        throttleTime(500, undefined, { leading: true, trailing: true }),
      );
  }),
  withImmediateValueOrDefault([]),
  shareAndHold(),
);
